import { AnimatePresence, motion } from "framer-motion";
import { useContext } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { ReactComponent as Close } from "../../assets/svg/close.svg";
import {
  BORDER_COLORS,
  COLORS,
  LAYER_COLORS,
  TEXT_COLORS,
} from "../../constants/design/colors";
import { FONT_CSS } from "../../constants/design/fontSize";
import { BORDER_RADIUS, SPACING } from "../../constants/design/spacing";
import { DepositWithdrawCollaterals } from "../../constants/addresses";
import { ConnectWalletContext } from "../../contexts/ConnectWalletContext";
import useKeydownHandler, {
  KeydownHandlerKeyEnum,
} from "../../hooks/useKeydownHandler";
import { getTxExplorerUrl } from "../../utils/chain";
import { AutoFaucetTransaction } from "../../hooks/api/faucet/useAutomaticFaucet";
import { ChainIdEnum } from "../../enums/chain";
import { Button, ButtonThemeEnum } from "../Buttons/styles";

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
`;

const DialogWrapper = styled(motion.div)`
  background: ${LAYER_COLORS.two};
  border: 1px solid ${BORDER_COLORS.one};
  border-radius: ${BORDER_RADIUS.lg}px;
  width: 400px;
  max-width: 90vw;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${SPACING.three}px;
  border-bottom: 1px solid ${BORDER_COLORS.one};
  gap: ${SPACING.two}px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.two}px;
`;

// Success is the one place the semantic green belongs; the title and the call
// to action stay neutral/accent like every other dialog (DESIGN.md: one accent).
const IconWrapper = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLORS.positive.one};

  svg {
    width: 100%;
    height: 100%;
  }
`;

const Title = styled.span`
  ${FONT_CSS.header.seven}
  color: ${TEXT_COLORS.one};
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${BORDER_RADIUS.sm}px;
  transition: background 0.15s ease;

  &:hover {
    background: ${COLORS.white.two};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const Content = styled.div`
  padding: ${SPACING.three}px;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.three}px;
`;

const Description = styled.div`
  ${FONT_CSS.body.five}
  color: ${TEXT_COLORS.two};
`;

const TransactionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.two}px;
`;

const TransactionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.two}px;
  padding: ${SPACING.two}px ${SPACING.three}px;
  background: ${LAYER_COLORS.one};
  border: 1px solid ${BORDER_COLORS.one};
  border-radius: ${BORDER_RADIUS.md}px;
`;

const TransactionAmount = styled.span`
  ${FONT_CSS.data.four}
  color: ${TEXT_COLORS.one};
  font-feature-settings: "tnum";
`;

const TransactionLink = styled.a`
  ${FONT_CSS.body.six}
  color: ${COLORS.highlight.one};

  &:hover {
    text-decoration: underline;
  }
`;

const Footer = styled.div`
  display: flex;
  gap: ${SPACING.two}px;
  padding: ${SPACING.three}px;
  border-top: 1px solid ${BORDER_COLORS.one};

  > button {
    flex: 1;
  }
`;

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M20 6L9 17l-5-5"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DEPOSITABLE = Object.values(DepositWithdrawCollaterals) as string[];

/**
 * The faucet token to preselect in the deposit flow: the quote (USDC) leg when
 * there is one, since that is what every market is margined and quoted in.
 */
export const depositAssetFor = (
  legs: AutoFaucetTransaction[]
): DepositWithdrawCollaterals | undefined => {
  const tokens = legs.map((tx) => tx.token?.toUpperCase());
  const pick =
    tokens.find((t) => t === DepositWithdrawCollaterals.Usdc) ??
    tokens.find((t) => t !== undefined && DEPOSITABLE.includes(t));
  return pick as DepositWithdrawCollaterals | undefined;
};

export interface FaucetOnboardingDialogProps {
  open: boolean;
  transactions?: AutoFaucetTransaction[];
  chainId?: ChainIdEnum;
  onClose: () => void;
}

/**
 * Shown once the automatic testnet faucet lands. The faucet pays the WALLET;
 * orders trade against the exchange balance, so the funds are not tradable until
 * deposited. The old copy said "No action needed — start trading" and its button
 * only closed the dialog, leaving a new trader with an empty Available to Trade
 * and no idea why. The primary action now opens the deposit flow for the token
 * that just arrived.
 */
const FaucetOnboardingDialog = ({
  open,
  transactions,
  chainId,
  onClose,
}: FaucetOnboardingDialogProps) => {
  useKeydownHandler(KeydownHandlerKeyEnum.ESC, onClose);
  const { openDepositFlow } = useContext(ConnectWalletContext);

  const legs = (transactions ?? []).filter((tx) => tx && tx.txHash);
  const depositAsset = depositAssetFor(legs);

  const onDeposit = () => {
    onClose();
    openDepositFlow(depositAsset);
  };

  return createPortal(
    <AnimatePresence>
      {open && (
        <Overlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onClick={onClose}
        >
          <DialogWrapper
            role="dialog"
            aria-modal="true"
            aria-labelledby="faucet-dialog-title"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            onClick={(e) => e.stopPropagation()}
          >
            <Header>
              <HeaderLeft>
                <IconWrapper aria-hidden>
                  <CheckIcon />
                </IconWrapper>
                <Title id="faucet-dialog-title">Testnet funds sent</Title>
              </HeaderLeft>
              <CloseButton onClick={onClose} aria-label="Close">
                <Close />
              </CloseButton>
            </Header>
            <Content>
              <Description>
                We sent testnet funds to your wallet. Deposit them into your
                trading account to start placing orders.
              </Description>
              {legs.length > 0 && (
                <TransactionList>
                  {legs.map((tx) => (
                    <TransactionRow key={tx.txHash}>
                      <TransactionAmount>
                        {tx.amount} {tx.token}
                      </TransactionAmount>
                      <TransactionLink
                        href={getTxExplorerUrl(tx.txHash, chainId)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View transaction
                      </TransactionLink>
                    </TransactionRow>
                  ))}
                </TransactionList>
              )}
            </Content>
            <Footer>
              <Button buttonTheme={ButtonThemeEnum.NEUTRAL2} onClick={onClose}>
                Later
              </Button>
              <Button buttonTheme={ButtonThemeEnum.HIGHLIGHT} onClick={onDeposit}>
                {depositAsset ? `Deposit ${depositAsset}` : "Deposit"}
              </Button>
            </Footer>
          </DialogWrapper>
        </Overlay>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default FaucetOnboardingDialog;
